import { SPEC_V1_SHA256, specV1, traitsV1, type Trait } from '@equiview/protocol';
import { useEffect, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { FotoDaMidia } from '../components/FotoDaMidia.js';
import { Marca } from '../components/Marca.js';
import { MarcaDeOrigem, MarcaForaDoProtocolo } from '../components/OrigemDoValor.js';
import { Aguardar, Botao, BotaoLink, EstadoVazio } from '../components/ui/index.js';
import {
  classeNaData,
  formatarData,
  formatarNascimento,
  formatarValor,
  hojeIso,
  idadeDoAnimal,
  indicesDaFicha,
  ORDEM_GRUPOS,
  ROTULO_CLASSE,
  ROTULO_GRUPO,
  ROTULO_INDICE,
  ROTULO_LEITURA,
  ROTULO_SEXO,
  ROTULO_VISTA,
  unidadeCurta,
} from '../dados/catalogo.js';
import { useConsulta, useFonte } from '../dados/contexto.js';
import type { Animal, Avaliacao, Haras, Medida, Midia } from '../dados/fonte.js';
import {
  fraseProle,
  MEDIDAS_DA_PROLE,
  montarPopulacao,
  posicaoNaIdade,
  proleNaMedida,
  type Populacao,
} from '../dados/populacao.js';

/** Medidas comparadas com o esperado para a idade, no laudo. */
const MEDIDAS_DE_POSICAO = ['A_CER', 'A_GAR', 'C_COR', 'PE_TOR', 'PE_CAN'] as const;

/**
 * Laudo individual: uma folha A4 feita para imprimir. O "Imprimir → Salvar
 * como PDF" do navegador gera o arquivo — funciona offline e usa os mesmos
 * calculos da ficha, entao laudo e tela nunca divergem.
 */
export function Laudo() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const fonte = useFonte();
  const consulta = useConsulta(
    async (f) => {
      const [animal, todos, todas, haras] = await Promise.all([f.animal(id), f.listarAnimais(), f.avaliacoes(), f.haras()]);
      const medidas = await f.medidas(todas.map((a) => a.id));
      const doAnimal = todas.filter((a) => a.animal_id === id);
      const ficha = doAnimal.find((a) => a.id === params.get('ficha')) ?? doAnimal.at(-1) ?? null;
      const midias = ficha ? await f.midias(ficha.id) : [];
      const daFicha = medidas.filter((m) => m.assessment_id === ficha?.id);
      return { animal, todos, ficha, haras, midias, daFicha, pop: montarPopulacao(todos, todas, medidas) };
    },
    [id, params.get('ficha')],
  );

  // O laudo e papel: sempre no tema claro, tambem na tela. Devolve o tema ao sair.
  useEffect(() => {
    const raiz = document.documentElement;
    const antes = raiz.dataset.theme;
    raiz.dataset.theme = 'light';
    return () => {
      if (antes) raiz.dataset.theme = antes;
      else delete raiz.dataset.theme;
    };
  }, []);

  return (
    <div className="min-h-dvh bg-superficie-2 print:bg-white">
      <Aguardar consulta={consulta}>
        {({ animal, todos, ficha, haras, midias, daFicha, pop }) =>
          !animal || !ficha ? (
            <div className="p-6">
              <EstadoVazio titulo="Sem avaliação para o laudo" acao={<BotaoLink to={`/animais/${id}`}>Voltar à ficha</BotaoLink>} />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-center gap-2 border-b border-borda bg-superficie px-4 py-3 print:hidden">
                <Botao variante="principal" onClick={() => window.print()}>
                  Imprimir / salvar PDF
                </Botao>
                <BotaoLink to={`/animais/${animal.id}?ficha=${ficha.id}`}>Voltar à ficha</BotaoLink>
                <span className="w-full text-center text-xs text-apagado sm:w-auto">
                  No diálogo de impressão, escolha "Salvar como PDF".
                </span>
              </div>
              <Folha
                animal={animal}
                todos={todos}
                ficha={ficha}
                haras={haras}
                midias={midias}
                medidas={daFicha}
                pop={pop}
                ficticio={fonte.ficticia}
              />
            </>
          )
        }
      </Aguardar>
    </div>
  );
}

function Folha({
  animal,
  todos,
  ficha,
  haras,
  midias,
  medidas,
  pop,
  ficticio,
}: {
  animal: Animal;
  todos: readonly Animal[];
  ficha: Avaliacao;
  haras: Haras | null;
  midias: readonly Midia[];
  medidas: readonly Medida[];
  pop: Populacao;
  ficticio: boolean;
}) {
  const valores = pop.valores.get(ficha.id) ?? {};
  const indices = indicesDaFicha(valores);
  const fechada = ficha.status === 'fechada' || ficha.status === 'validada';
  const pai = todos.find((a) => a.id === animal.sire_id);
  const mae = todos.find((a) => a.id === animal.dam_id);
  const filhos = todos.filter((a) => a.sire_id === animal.id || a.dam_id === animal.id);
  const prole = filhos.length ? MEDIDAS_DA_PROLE.map((c) => proleNaMedida(pop, filhos, c)) : [];
  const fotos = (['perfil_esquerdo', 'frente', 'tras'] as const)
    .map((v) => midias.filter((m) => m.kind === 'foto' && m.view === v).at(-1))
    .filter((m): m is Midia => !!m);
  // A marca de origem de cada valor (so o animal real da demonstracao tem).
  const doTrait = (codigo: string) => medidas.filter((m) => m.trait_code === codigo);
  const extras = animal.real?.extras ?? [];
  const temOrigem = extras.length > 0 || medidas.some((m) => (m.origem && m.origem !== 'campo') || m.conferir);
  const canelaAnterior = extras.some((e) => /canela posterior/i.test(e.rotulo));

  return (
    <article className="mx-auto max-w-[210mm] space-y-6 bg-white px-4 py-6 text-[13px] text-abismo sm:my-6 sm:px-[14mm] sm:py-[12mm] sm:shadow-menu print:my-0 print:max-w-none print:p-0 print:shadow-none">
      {animal.real ? (
        <div className="border-2 border-ok px-3 py-1.5 text-center text-sm">
          <p className="font-semibold tracking-widest text-ok uppercase">Animal real · coleta: {animal.real.coleta}</p>
          <p className="text-xs text-apagado">
            Valores marcados vieram da foto ou foram estimados. A faixa esperada vem dos animais fictícios da demonstração.
          </p>
        </div>
      ) : (
        ficticio && (
          <p className="border-2 border-erro px-3 py-1.5 text-center text-sm font-semibold tracking-widest text-erro uppercase">
            Dados fictícios — só para demonstração
          </p>
        )
      )}

      <header className="flex flex-col gap-3 border-b-2 border-abismo pb-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6 print:flex-row print:items-start print:justify-between print:gap-6">
        <div className="space-y-2">
          <Marca tom="claro" className="h-9" />
          <p className="text-xs text-apagado">Avaliação morfofuncional equina</p>
        </div>
        <div className="sm:text-right print:text-right">
          <h1 className="font-serif text-2xl font-semibold">Laudo morfométrico</h1>
          <p className="text-xs text-apagado">Emitido em {formatarData(hojeIso())}</p>
          {haras && <p className="text-xs text-apagado">{haras.name}</p>}
        </div>
      </header>

      <section className="flex flex-col items-start gap-3 break-inside-avoid sm:flex-row sm:justify-between sm:gap-6 print:flex-row print:justify-between print:gap-6">
        <div>
          <h2 className="font-serif text-2xl font-semibold sm:text-3xl print:text-3xl">{animal.name}</h2>
          <p className="font-mono text-sm">{animal.abccmm_registry ?? 'Sem registro'}</p>
        </div>
        {fechada ? (
          <span className="shrink-0 border-2 border-latao px-3 py-1.5 text-center text-xs font-semibold tracking-widest text-latao-texto uppercase">
            Animal avaliado
            <br />
            <span className="font-normal tracking-normal normal-case">{formatarData(ficha.visit_date)}</span>
          </span>
        ) : (
          <span className="shrink-0 border border-aviso px-3 py-1.5 text-xs text-aviso">Ficha em aberto: laudo provisório</span>
        )}
      </section>

      <section className="grid gap-6 break-inside-avoid sm:grid-cols-[1fr_auto]">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 sm:grid-cols-3">
          <Item rotulo="Sexo">{ROTULO_SEXO[animal.sex]}</Item>
          <Item rotulo="Nascimento">{formatarNascimento(animal)}</Item>
          <Item rotulo="Idade na avaliação">{idadeDoAnimal(animal, ficha.visit_date)}</Item>
          <Item rotulo="Classe na avaliação">{ROTULO_CLASSE[classeNaData(animal, todos, ficha.visit_date)]}</Item>
          <Item rotulo="Pelagem">{animal.coat_color ?? '—'}</Item>
          <Item rotulo="Marcha declarada">
            {animal.marcha_type_declared === 'batida' ? 'Batida' : animal.marcha_type_declared === 'picada' ? 'Picada' : '—'}
          </Item>
          <Item rotulo="Pai">{pai?.name ?? '—'}</Item>
          <Item rotulo="Mãe">{mae?.name ?? '—'}</Item>
          <Item rotulo="Avaliação">
            {formatarData(ficha.visit_date)} · {ficha.protocol_level === 1 ? 'Nível 1 (censo)' : 'Nível 2 (completa)'}
          </Item>
        </dl>
      </section>

      {fotos.length > 0 && (
        <section className="break-inside-avoid">
          <Titulo>Fotos</Titulo>
          <div className="grid grid-cols-3 gap-3">
            {fotos.map((m) => (
              <figure key={m.id} className="space-y-1">
                <FotoDaMidia midia={m} alt={m.view ? ROTULO_VISTA[m.view] : 'Foto'} className="aspect-4/3 w-full border border-borda" />
                <figcaption className="text-xs text-apagado">{m.view ? ROTULO_VISTA[m.view] : 'Foto'}</figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}

      <section className="break-inside-avoid">
        <Titulo>Índices</Titulo>
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5 print:grid-cols-5">
          {(Object.keys(ROTULO_INDICE) as (keyof typeof ROTULO_INDICE)[]).map((k) => (
            <Item key={k} rotulo={ROTULO_INDICE[k]}>
              <span className="numero">
                {indices[k] == null ? '—' : indices[k]!.toLocaleString('pt-BR', { maximumFractionDigits: 2, minimumFractionDigits: 2 })}
              </span>
            </Item>
          ))}
        </dl>
      </section>

      <section className="break-inside-avoid">
        <Titulo>Posição em relação ao esperado para a idade</Titulo>
        <ul className="divide-y divide-borda border-y border-borda sm:hidden print:hidden">
          {MEDIDAS_DE_POSICAO.map((codigo) => {
            const t = traitsV1.find((x) => x.code === codigo)!;
            const p = posicaoNaIdade(pop, animal, ficha, codigo);
            if (!p) return null;
            return (
              <li key={codigo} className="py-2">
                <p className="flex justify-between gap-3">
                  <span className="font-medium">{t.label}</span>
                  <span className="numero whitespace-nowrap">
                    {formatarValor(p.valor, t)}
                    {unidadeCurta(t)}
                    <MarcaDeOrigem medidas={doTrait(codigo)} />
                  </span>
                </p>
                <p className="text-xs text-apagado">
                  {p.desvio ? ROTULO_LEITURA[p.desvio.leitura] : 'sem referência para a idade'}
                  {p.esperado &&
                    ` · esperado ${formatarValor(p.esperado.media - p.esperado.dp, t)}–${formatarValor(p.esperado.media + p.esperado.dp, t)}${unidadeCurta(t)}`}
                </p>
              </li>
            );
          })}
        </ul>
        <table className="hidden w-full border-collapse sm:table print:table">
          <thead>
            <tr className="border-b border-abismo text-left text-xs text-apagado">
              <th className="py-1 font-medium">Medida</th>
              <th className="py-1 text-right font-medium">Valor</th>
              <th className="py-1 text-right font-medium">Faixa esperada</th>
              <th className="py-1 pl-4 font-medium">Leitura</th>
            </tr>
          </thead>
          <tbody>
            {MEDIDAS_DE_POSICAO.map((codigo) => {
              const t = traitsV1.find((x) => x.code === codigo)!;
              const p = posicaoNaIdade(pop, animal, ficha, codigo);
              if (!p) return null;
              return (
                <tr key={codigo} className="border-b border-borda">
                  <td className="py-1">{t.label}</td>
                  <td className="numero py-1 text-right whitespace-nowrap">
                    {formatarValor(p.valor, t)}
                    {unidadeCurta(t)}
                    <MarcaDeOrigem medidas={doTrait(codigo)} />
                  </td>
                  <td className="numero py-1 text-right">
                    {p.esperado
                      ? `${formatarValor(p.esperado.media - p.esperado.dp, t)}–${formatarValor(p.esperado.media + p.esperado.dp, t)}${unidadeCurta(t)}`
                      : '—'}
                  </td>
                  <td className="py-1 pl-4">{p.desvio ? ROTULO_LEITURA[p.desvio.leitura] : 'sem referência para a idade'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="mt-1 text-xs text-apagado">
          Faixa esperada: média ± 1 desvio-padrão dos animais medidos na base Equiview com idade próxima (o próprio animal
          não entra). Método provisório, a revisar com o crescimento da base.
        </p>
      </section>

      <section>
        <Titulo>Medidas</Titulo>
        <div className="gap-6 sm:columns-2 print:columns-2">
          {ORDEM_GRUPOS.map((grupo) => {
            const doGrupo = traitsV1.filter((t) => t.group === grupo && temValor(t, valores));
            const extrasDoGrupo = extras.filter((e) => e.grupo === grupo);
            if (doGrupo.length === 0 && extrasDoGrupo.length === 0) return null;
            return (
              <div key={grupo} className="mb-4 break-inside-avoid">
                <p className="mb-1 text-xs font-semibold tracking-wide text-apagado uppercase">{ROTULO_GRUPO[grupo]}</p>
                <table className="w-full border-collapse">
                  <tbody>
                    {doGrupo.map((t) => (
                      <tr key={t.code} className="border-b border-borda">
                        <td className="py-0.5">{t.code === 'PE_CAN' && canelaAnterior ? `${t.label} (anterior)` : t.label}</td>
                        <td className="numero py-0.5 text-right whitespace-nowrap">
                          {textoDoValor(t, valores)}
                          <MarcaDeOrigem medidas={doTrait(t.code)} />
                        </td>
                      </tr>
                    ))}
                    {extrasDoGrupo.map((e) => (
                      <tr key={e.rotulo} className="border-b border-borda">
                        <td className="py-0.5">{e.rotulo}</td>
                        <td className="numero py-0.5 text-right whitespace-nowrap">
                          {e.valor.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} {e.unidade}
                          <MarcaForaDoProtocolo original={e.original} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      </section>

      {prole.length > 0 && (
        <section className="break-inside-avoid">
          <Titulo>Prole</Titulo>
          <ul className="space-y-0.5">
            {prole.map((p) => (
              <li key={p.codigo}>{fraseProle(p)}</li>
            ))}
          </ul>
          <p className="mt-1 text-xs text-apagado">
            Cada filho comparado com o esperado para a idade dele, pela última avaliação. Média simples, não é avaliação
            genética.
          </p>
        </section>
      )}

      {temOrigem && (
        <section className="break-inside-avoid text-xs text-apagado">
          <Titulo>Origem dos valores</Titulo>
          <p>
            Sem marca: medido no animal. <MarcaDeOrigem medidas={[{ origem: 'foto' }]} /> ângulo lido na foto de perfil
            pela fotogrametria. <MarcaDeOrigem medidas={[{ origem: 'estimado' }]} /> não medido: estimado por proporção ou
            avaliação visual. <MarcaDeOrigem medidas={[{ conferir: 'sim' }]} /> valor a conferir com quem mediu.
            {extras.length > 0 && (
              <>
                {' '}
                <MarcaForaDoProtocolo /> medido, mas a ficha v1.0 não tem essa medida.
              </>
            )}
          </p>
        </section>
      )}

      <footer className="space-y-0.5 border-t-2 border-abismo pt-3 font-mono text-[10px] text-apagado">
        <p className="break-all">
          Protocolo {specV1.code} · sha256 {SPEC_V1_SHA256}
        </p>
        <p>Avaliação {ficha.id}</p>
        <p className="font-sans">Equiview · avaliação morfofuncional equina. Este laudo reflete as medidas da data indicada.</p>
      </footer>
    </article>
  );
}

function Titulo({ children }: { children: string }) {
  return <h3 className="mb-2 border-b border-borda pb-1 font-serif text-base font-semibold">{children}</h3>;
}

function Item({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-apagado sm:text-[11px] print:text-[11px]">{rotulo}</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}

const temValor = (t: Trait, v: Record<string, number | null>) =>
  t.sided ? v[`${t.code}_E`] !== undefined || v[`${t.code}_D`] !== undefined : v[t.code] !== undefined;

function textoDoValor(t: Trait, v: Record<string, number | null>): string {
  const um = (x: number | null | undefined) => (x == null ? 'N/A' : `${formatarValor(x, t)}${unidadeCurta(t)}`);
  return t.sided ? `E ${um(v[`${t.code}_E`])} · D ${um(v[`${t.code}_D`])}` : um(v[t.code]);
}
